"use client";
import React, { useState } from "react";
import { useApprovalController } from "./useApprovalController";
import { PageContainer } from "@/src/components/layout/page-container";
import { Text } from "@/src/components/information/text";
import { Button } from "@/src/components/input/button";
import { Card } from "@/src/components/container/card";
import { Stepper } from "@/src/components/navigation/stepper";
import { Input } from "@/src/components/input/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/src/components/input/select";
import { Textarea } from "@/src/components/input/textarea";
import { Checkbox } from "@/src/components/input/checkbox";
import { Badge } from "@/src/components/information/badge";
import { FormField } from "@/src/components/input/form-field";
import { Alert } from "@/src/components/information/alert";
import { SectionCard } from "@/src/components/container/section-card";
import { Section } from "@/src/components/layout/section";
import { CheckCircle, AlertCircle, Trash2 } from "lucide-react";

export function ApprovalPage() {
  const controller = useApprovalController();
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async () => {
    try {
      await controller.submit();
      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);
    } catch (err) {
      console.error("Submit error:", err);
    }
  };

  const renderStepContent = () => {
    switch (controller.currentStep) {
      case 0:
        return (
          <Section title="Request Details" description="Enter vendor and basic approval information">
            <div className="space-y-theme-4">
              <FormField label="Vendor Name" htmlFor="vendor" required>
                <Input
                  id="vendor"
                  placeholder="Enter vendor name"
                  value={controller.formData.vendor}
                  onChange={(e) => controller.updateFormData({ vendor: e.target.value })}
                  aria-required="true"
                />
              </FormField>

              <FormField label="Description" htmlFor="description" required>
                <Textarea
                  id="description"
                  placeholder="Describe the purpose of this request"
                  value={controller.formData.description}
                  onChange={(e) => controller.updateFormData({ description: e.target.value })}
                  rows={4}
                  aria-required="true"
                />
              </FormField>

              <div className="grid grid-cols-2 gap-theme-3">
                <FormField label="Amount" htmlFor="amount" required>
                  <Input
                    id="amount"
                    type="number"
                    placeholder="0.00"
                    value={controller.formData.amount}
                    onChange={(e) => controller.updateFormData({ amount: parseFloat(e.target.value) || 0 })}
                    aria-required="true"
                  />
                </FormField>

                <FormField label="Cost Center" htmlFor="costCenter" required>
                  <Input
                    id="costCenter"
                    placeholder="e.g., IT-001"
                    value={controller.formData.costCenter}
                    onChange={(e) => controller.updateFormData({ costCenter: e.target.value })}
                    aria-required="true"
                  />
                </FormField>
              </div>

              <FormField label="Urgency" htmlFor="urgency">
                <Select
                  value={controller.formData.urgency}
                  onValueChange={(value: string) => controller.updateFormData({ urgency: value as "low" | "medium" | "high" })}
                >
                  <SelectTrigger id="urgency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Additional Notes" htmlFor="notes">
                <Textarea
                  id="notes"
                  placeholder="Add any additional notes"
                  value={controller.formData.notes}
                  onChange={(e) => controller.updateFormData({ notes: e.target.value })}
                  rows={3}
                />
              </FormField>
            </div>
          </Section>
        );

      case 1:
        return (
          <Section title="Line Items" description="Review itemized costs for this request">
            <SectionCard contentClassName="p-theme-4">
              <div className="space-y-theme-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-theme-3 px-theme-4 font-semibold">Description</th>
                        <th className="text-right py-theme-3 px-theme-4 font-semibold">Quantity</th>
                        <th className="text-right py-theme-3 px-theme-4 font-semibold">Unit Price</th>
                        <th className="text-right py-theme-3 px-theme-4 font-semibold">Total</th>
                        <th className="text-center py-theme-3 px-theme-4 font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {controller.formData.lineItems.map((item) => (
                        <tr key={item.id} className="border-b border-border hover:bg-accent/40">
                          <td className="py-theme-3 px-theme-4">{item.description}</td>
                          <td className="py-theme-3 px-theme-4 text-right">{item.quantity}</td>
                          <td className="py-theme-3 px-theme-4 text-right">${item.unitPrice.toLocaleString()}</td>
                          <td className="py-theme-3 px-theme-4 text-right font-semibold">${item.totalPrice.toLocaleString()}</td>
                          <td className="py-theme-3 px-theme-4 text-center">
                            <button
                              onClick={() => controller.removeLineItem(item.id)}
                              className="text-destructive hover:text-destructive/80"
                              aria-label="Remove line item"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="pt-theme-4 border-t border-border">
                  <div className="flex justify-between items-center">
                    <Text className="text-h4 font-semibold">Total Amount:</Text>
                    <Text className="text-h4 font-semibold text-primary">
                      ${controller.formData.lineItems.reduce((sum, item) => sum + item.totalPrice, 0).toLocaleString()}
                    </Text>
                  </div>
                </div>
              </div>
            </SectionCard>
          </Section>
        );

      case 2:
        return (
          <Section title="Documents" description="Upload and verify all required documents">
            <div className="space-y-theme-4">
              <div className="space-y-theme-3">
                {controller.formData.documents.map((doc) => (
                  <Card key={doc.id} className="p-theme-3 flex items-start gap-theme-3">
                    <Checkbox
                      checked={doc.completed}
                      onCheckedChange={() => controller.toggleDocumentCompletion(doc.id)}
                      disabled={doc.required && !doc.completed}
                      aria-label={`Mark ${doc.name} as completed`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-theme-2">
                        <Text className="text-body font-medium">
                          {doc.name}
                        </Text>
                        {doc.required && (
                          <Badge variant="destructive" size="sm">
                            Required
                          </Badge>
                        )}
                        {doc.completed && <CheckCircle className="h-5 w-5 text-success" />}
                      </div>
                      {doc.url && (
                        <a href={doc.url} className="text-primary text-body hover:underline mt-theme-1">
                          View document
                        </a>
                      )}
                    </div>
                  </Card>
                ))}
              </div>

              {controller.formData.documents.filter((d) => !d.completed && d.required).length > 0 && (
                <Alert
                  variant="warning"
                  title="Missing Required Documents"
                  description="All required documents must be completed before proceeding."
                />
              )}
            </div>
          </Section>
        );

      case 3:
        return (
          <Section title="Review & Submit" description="Verify all information before submitting your request">
            <div className="space-y-theme-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-theme-3">
                <Card className="p-theme-3">
                  <Text className="text-xs text-muted-foreground">
                    Vendor
                  </Text>
                  <Text className="text-body font-semibold mt-theme-1">
                    {controller.formData.vendor}
                  </Text>
                </Card>

                <Card className="p-theme-3">
                  <Text className="text-xs text-muted-foreground">
                    Total Amount
                  </Text>
                  <Text className="text-body font-semibold mt-theme-1">
                    ${controller.formData.amount.toLocaleString()}
                  </Text>
                </Card>

                <Card className="p-theme-3">
                  <Text className="text-xs text-muted-foreground">
                    Cost Center
                  </Text>
                  <Text className="text-body font-semibold mt-theme-1">
                    {controller.formData.costCenter}
                  </Text>
                </Card>

                <Card className="p-theme-3">
                  <Text className="text-xs text-muted-foreground">
                    Urgency
                  </Text>
                  <Badge
                    variant={controller.formData.urgency === "high" ? "destructive" : controller.formData.urgency === "medium" ? "warning" : "success"}
                    size="sm"
                    className="mt-theme-1"
                  >
                    {controller.formData.urgency.charAt(0).toUpperCase() + controller.formData.urgency.slice(1)}
                  </Badge>
                </Card>
              </div>

              {/* Description */}
              <Card className="p-theme-3">
                <Text className="text-xs text-muted-foreground">
                  Description
                </Text>
                <Text className="text-body mt-theme-2">
                  {controller.formData.description}
                </Text>
              </Card>

              {/* Line Items Summary */}
              <Card className="p-theme-3">
                <Text className="text-h4 font-semibold mb-theme-3">
                  Line Items ({controller.formData.lineItems.length})
                </Text>
                <div className="space-y-theme-2">
                  {controller.formData.lineItems.map((item) => (
                    <div key={item.id} className="flex justify-between text-body">
                      <Text className="text-body">{item.description}</Text>
                      <Text className="text-body font-semibold">
                        ${item.totalPrice.toLocaleString()}
                      </Text>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Documents Summary */}
              <Card className="p-theme-3">
                <Text className="text-h4 font-semibold mb-theme-3">
                  Documents ({controller.formData.documents.filter((d) => d.completed).length}/{controller.formData.documents.length})
                </Text>
                <div className="space-y-theme-2">
                  {controller.formData.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-theme-2">
                      <CheckCircle className={`h-4 w-4 ${doc.completed ? "text-success" : "text-muted-foreground/40"}`} />
                      <Text className="text-body">{doc.name}</Text>
                      {doc.required && (
                        <Badge variant="destructive" size="sm">
                          Required
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </Section>
        );

      default:
        return null;
    }
  };

  return (
    <PageContainer className="!px-0 !py-0">
      <div className="space-y-theme-6">
        {/* Header */}
        <div className="animate-fade-in-up">
          <Text className="text-h2 font-bold mb-theme-2">
            Approval Request
          </Text>
          <Text className="text-body text-muted-foreground">
            Complete all steps to submit your approval request
          </Text>
        </div>

        {/* Stepper */}
        <Stepper
          steps={controller.steps.map((s) => ({ title: s.label, description: s.description }))}
          activeStep={controller.currentStep}
          onStepClick={controller.goToStep}
        />

        {/* Success Message */}
        {submitSuccess && (
          <Alert
            variant="success"
            title="Approval request submitted successfully!"
            description="Your request has been sent for review. You will receive updates via email."
          />
        )}

        {/* Error Message */}
        {controller.error && (
          <Alert
            variant="destructive"
            title="Error"
            description={controller.error}
          />
        )}

        {/* Content Card */}
        <Card className="p-theme-6 animate-fade-in-up">{renderStepContent()}</Card>

        {/* Action Bar */}
        <div className="flex gap-theme-3 justify-between">
          <Button
            variant="outline"
            onClick={controller.previousStep}
            disabled={!controller.canGoBack}
          >
            Previous
          </Button>

          <div className="flex gap-theme-3">
            {controller.isLastStep ? (
              <Button
                onClick={handleSubmit}
                disabled={controller.loading}
                className="bg-primary hover:bg-primary/90"
              >
                {controller.loading ? "Submitting..." : "Submit Request"}
              </Button>
            ) : (
              <Button
                onClick={controller.nextStep}
                disabled={!controller.canProceed}
              >
                Next Step
              </Button>
            )}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
